import { type Ref, computed, watchEffect } from 'vue'
import { gql, type WatchQueryFetchPolicy } from '@apollo/client/core'
import { useQuery, useMutation } from '/@/core/graphql'
import type { MockupType, SetupState, Vizi, ViziVersion } from '../types'
import type { RoomType } from '/@/common/rooms/types'
import { useToast } from '/@/core/elements/toast'
import { throwVizError } from '/@/core/errors'
import { dailyPlayUpdateFragment } from '/@/common/daily-play/api'
import { designChallengeFragment } from '/@/common/designChallenges/fragments'
import {
  viziFragment,
  viziVersionFragment,
  viziVersionWithDataFragment,
} from './fragments'
import { evictFromCache } from '/@/utils/apollo'
import type { ProjectPermissionType } from '/@/common/projects/types'
import { pageInfoFragment, type PaginatedList } from '/@/utils/pagination'
import { getAccountsDataQuery } from '/@/common/accounts/api'
import type { Mockup } from '/@/common/things/types'

export interface Modifiers {
  projectIds?: string[]
  permissionTypes?: ProjectPermissionType[]
  designerIds?: string[]
  mockupTypes?: MockupType[]
  text?: string
  first?: number
  after?: string
}

export const getVizisListGqlQuery = gql`
  ${viziFragment}
  ${pageInfoFragment}
  query getDesignerMockupList(
    $projectIds: [String!]
    $permissionTypes: [ProjectPermissionType!]
    $designerIds: [String!]
    $mockupTypes: [MockupType!]
    $text: String
    $first: Int
    $after: String
  ) {
    user {
      id
      designer {
        id
        mockupList(
          projectIds: $projectIds
          permissionTypes: $permissionTypes
          designerIds: $designerIds
          mockupTypes: $mockupTypes
          text: $text
          first: $first
          after: $after
        ) {
          pageInfo {
            ...PageInfoProps
          }
          edges {
            node {
              ...ViziProps
            }
          }
        }
      }
    }
  }
`

export const getVizisListExistsGqlQuery = gql`
  query getDesignerMockupListExists(
    $projectIds: [String!]
    $permissionTypes: [ProjectPermissionType!]
    $designerIds: [String!]
    $mockupTypes: [MockupType!]
    $text: String
  ) {
    user {
      id
      designer {
        id
        hasMockups(
          projectIds: $projectIds
          permissionTypes: $permissionTypes
          designerIds: $designerIds
          mockupTypes: $mockupTypes
          text: $text
        )
      }
    }
  }
`

export function useVizisList(modifiers: Ref<Modifiers>) {
  const { result, loading, refetch, fetchMore } = useQuery<{
    user?: {
      designer: {
        mockupList: PaginatedList<Mockup>
      }
    }
  }>(
    getVizisListGqlQuery,
    computed(() => ({
      projectIds: modifiers.value.projectIds,
      permissionTypes: modifiers.value.permissionTypes,
      designerIds: modifiers.value.designerIds,
      mockupTypes: modifiers.value.mockupTypes,
      text: modifiers.value.text,
      first: modifiers.value.first,
      after: modifiers.value.after,
    })),
    {
      notifyOnNetworkStatusChange: true,
    },
  )

  const mockupList = computed(() => result.value?.user?.designer?.mockupList)

  return { mockupList, loading, refetch, fetchMore }
}

export function useVizisListExists(modifiers: Ref<Modifiers>) {
  const { result, loading, refetch } = useQuery<{
    user?: {
      designer: {
        id: string
        hasMockups: boolean
      }
    }
  }>(
    getVizisListExistsGqlQuery,
    computed(() => ({
      projectIds: modifiers.value.projectIds,
      permissionTypes: modifiers.value.permissionTypes,
      designerIds: modifiers.value.designerIds,
      mockupTypes: modifiers.value.mockupTypes,
      text: modifiers.value.text,
    })),
    {
      notifyOnNetworkStatusChange: true,
    },
  )

  const hasMockups = computed(
    () => result.value?.user?.designer?.hasMockups ?? false,
  )

  return { hasMockups, loading, refetch }
}

export const getViziDataGqlQuery = gql`
  ${viziFragment}
  query getViziData($id: String!) {
    mockup(id: $id) {
      ...ViziProps
    }
  }
`

export function useViziData(
  id: Ref<string | null | undefined>,
  fetchPolicy?: WatchQueryFetchPolicy | undefined,
) {
  const toast = useToast()

  const { result, loading, error, refetch } = useQuery<{ mockup: Vizi }>(
    getViziDataGqlQuery,
    () => ({
      id: id.value,
    }),
    () => ({
      enabled: Boolean(id?.value),
      fetchPolicy: fetchPolicy ?? 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    }),
  )

  watchEffect(() => {
    if (error.value) {
      // We don't need to worry about 409s. They're handled gracefully with async error handlers.
      if (
        error.value.message &&
        error.value.message.includes('Received status code 409')
      )
        return

      toast.showError({ text: 'Something went wrong loading this Vizi.' })
      throwVizError('Something went wrong loading a Vizi', {
        jsErrorMessage: error.value.message,
        jsErrorStack: error.value.stack,
      })
    }
  })

  const vizi = computed(() => result.value?.mockup)

  return {
    vizi,
    loading,
    refetch,
    error,
  }
}

export function useViziVersionsData(id: Ref<string>) {
  const { result, loading, error } = useQuery<{ mockup: Vizi }>(
    gql`
      ${viziVersionFragment}
      query getViziVersionsData($id: String!) {
        mockup(id: $id) {
          versions {
            ...ViziVersionProps
          }
        }
      }
    `,
    {
      id,
    },
    () => ({
      enabled: Boolean(id?.value),
      fetchPolicy: 'cache-and-network',
    }),
  )

  const viziVersions = computed(() => result.value?.mockup?.versions ?? [])

  return {
    viziVersions,
    loading,
    error,
  }
}

export function useViziVersionData(id: Ref<string>) {
  const { result, loading, error } = useQuery<{ mockupVersion: ViziVersion }>(
    gql`
      ${viziVersionWithDataFragment}
      query getViziVersionData($id: String!) {
        mockupVersion(id: $id) {
          ...ViziVersionWithDataProps
        }
      }
    `,
    () => ({
      id: id.value,
    }),
  )

  const viziVersion = computed(() => result.value?.mockupVersion)

  return {
    viziVersion,
    loading,
    error,
  }
}

export interface ProductInput {
  productId: string
  skuId?: string
  imageUrl?: string
  hotspot?: { x: number; y: number }
  objectId: string
}
export interface PaletteInput {
  paletteId: string
  objectId: string
}
export interface ImageInput {
  imageUrl: string
  objectId: string
}
export interface FillInput {
  assetId?: string
  assetType?: string
  color?: string
  url?: string
  productId?: string
}
export interface StockImageInput {
  url?: string
  stockImageId?: string | null
  name?: string | null
  category?: string | null
  mockupFill?: FillInput | null
  objectId: string
}
export interface PointInput {
  x: number
  y: number
}
export interface FloorplanRoomInput {
  points: PointInput[]
  mockupFill?: FillInput
  objectId: string
}
export type RoomPartType = 'WALL' | 'FLOOR' | 'CEILING'
export interface RoomPartInput {
  roomPartType: RoomPartType
  mockupFill?: FillInput
  objectId: string
}
export interface MockupSize {
  pixelsPerCm: number
  heightPixels: number
  widthPixels: number
}
export interface MockupCreateInput {
  name: string
  data?: string
  dataUrl?: string
  imageUrl?: string
  projectId?: string
  sectionId?: string
  products?: ProductInput[]
  palettes?: PaletteInput[]
  images?: ImageInput[]
  stockImages?: StockImageInput[]
  floorplanRooms?: FloorplanRoomInput[]
  roomParts?: RoomPartInput[]
  dailyPlayId?: string
  taskId?: string
  mockupType: MockupType
  templateId?: string
  backgroundImageUrl?: string
  designChallengeId?: string
  vizVersion?: string
  roomType?: RoomType
  roomTypeExtra?: string
  size?: MockupSize
  fromViziId?: string
  setupState?: string
}
interface MockupNameInput {
  id: string
  name: string
}
interface MockupNoteInput {
  id: string
  note: string
}
interface MockupTypeInput {
  id: string
  mockupType: MockupType
}
interface ProjectMockupInput {
  projectId: string
  mockupId: string
  sectionId?: string
}

export interface OptionGroupsInput {
  groupId: string
  name?: string
  options: Array<{
    objectId: string
    name: string
    isSelected: boolean
  }>
}

// missing mockupType
export interface MockupDataInput {
  id: string
  /**
   * @deprecated use the dataUrl property instead
   */
  data?: string
  dataUrl?: string
  imageUrl: string
  /**
   * @deprecated use the options property instead
   */
  products?: ProductInput[]
  /**
   * @deprecated use the options property instead
   */
  palettes?: PaletteInput[]
  /**
   * @deprecated use the options property instead
   */
  images?: ImageInput[]
  stockImages?: StockImageInput[]
  floorplanRooms?: FloorplanRoomInput[]
  roomParts?: RoomPartInput[]
  optionGroups?: OptionGroupsInput[]
  latestVersionId?: string
  defaultBackgroundImageUrl?: string
  vizVersion?: string
  size?: MockupSize
  setupState: SetupState
}

interface MockupDesignChallengeInput {
  id: string
  designChallengeId: string
}

interface MockupDailyPlayInput {
  id: string
  dailyPlayId: string
}

interface MockupUpdateVisibilityInput {
  id: string
  visibility: Vizi['costVisibility']
}

interface RoomTypeInput {
  id: string
  roomType: RoomType
  roomTypeExtra?: string
}
interface TemplateInput {
  id: string
  templateId: string
  mockupTemplateId?: string
}

export function useVizApi() {
  const { mutate: create } = useMutation<{
    mockup: { create: { record?: { id?: string; designerId?: string } } }
  }>(gql`
    mutation createNewVizi($input: MockupCreateInput!) {
      mockup {
        create(input: $input) {
          record {
            id
            designerId
            projectId
          }
        }
      }
    }
  `)

  const { mutate: updateName } = useMutation(gql`
    mutation updateViziName($input: MockupNameInput!) {
      mockup {
        updateName(input: $input) {
          record {
            id
            name
          }
        }
      }
    }
  `)

  const { mutate: updateNote } = useMutation(gql`
    mutation updateViziNote($input: MockupNoteInput!) {
      mockup {
        updateNote(input: $input) {
          record {
            id
            note
          }
        }
      }
    }
  `)

  const { mutate: updateType } = useMutation(gql`
    mutation updateViziType($input: MockupTypeInput!) {
      mockup {
        updateType(input: $input) {
          record {
            id
            mockupType
          }
        }
      }
    }
  `)

  const { mutate: updateProject } = useMutation(gql`
    mutation updateViziProject($input: [ProjectMockupInput!]!) {
      mockup {
        updateProject(input: $input) {
          record {
            id
            project {
              id
              name
            }
            section {
              id
              name
            }
          }
        }
      }
    }
  `)

  const { mutate: updateData } = useMutation<{
    mockup: { updateData: { status: number; record?: Vizi | null } }
  }>(gql`
    mutation updateViziData($input: MockupDataInput!) {
      mockup {
        updateData(input: $input) {
          status
          record {
            id
            imageUrl
            setupState
            latestVersion {
              id
            }
          }
        }
      }
    }
  `)

  const { mutate: deleteVizi } = useMutation<{
    mockup: { delete: { status: number; recordId?: string } }
  }>(gql`
    mutation deleteVizi($input: String!) {
      mockup {
        delete(input: $input) {
          status
          recordId
        }
      }
    }
  `)

  const { mutate: updateDesignChallenge } = useMutation(gql`
    ${designChallengeFragment}
    mutation updateViziDesignChallenge($input: MockupDesignChallengeInput!) {
      mockup {
        updateDesignChallenge(input: $input) {
          record {
            id
            designChallenge {
              ...DesignChallengeProps
            }
          }
        }
      }
    }
  `)

  const { mutate: updateDailyPlay } = useMutation(gql`
    ${dailyPlayUpdateFragment}
    mutation updateViziDailyPlay($input: MockupDailyPlayInput!) {
      mockup {
        updateDailyPlay(input: $input) {
          record {
            id
            dailyPlay {
              ...DailyPlayUpdateFragment
            }
          }
        }
      }
    }
  `)

  const { mutate: sendReminder } = useMutation(gql`
    mutation sendViziReminder {
      mockup {
        sendReminder {
          status
        }
      }
    }
  `)

  const { mutate: updateVisibility } = useMutation(gql`
    mutation toggleProductCostVisibility(
      $id: String!
      $visibility: CostVisibility!
    ) {
      mockup {
        updateCostVisibility(id: $id, costVisibility: $visibility) {
          record {
            id
            costVisibility
          }
        }
      }
    }
  `)

  const { mutate: updateRoomType } = useMutation(gql`
    mutation updateRoomType($input: RoomTypeInput!) {
      mockup {
        updateRoomType(input: $input) {
          recordId
          status
          record {
            id
            roomType
            roomTypeExtra
            room {
              id
              name
            }
          }
        }
      }
    }
  `)
  const { mutate: updateTemplate } = useMutation<{
    mockup: {
      updateTemplate: {
        status: number
        record?: {
          id: string
          templateId?: string
          template?: { id: string; dataUrl?: string; dataRaw?: string }
        }
      }
    }
  }>(gql`
    mutation updateTemplate($input: MockupTemplateInput!) {
      mockup {
        updateTemplate(input: $input) {
          recordId
          status
          record {
            id
            templateId
            template {
              id
              dataUrl
              dataRaw
            }
          }
        }
      }
    }
  `)

  return {
    async create(input: MockupCreateInput): Promise<string | undefined> {
      const response = await create(
        { input },
        {
          update(cache, { data }) {
            const designerId = data?.mockup?.create?.record?.designerId
            const projectId = data?.mockup?.create?.record?.projectId
            if (designerId) {
              evictFromCache(cache, designerId, 'Designer', 'mockupList')
              evictFromCache(cache, designerId, 'Designer', 'hasMockups')
              evictFromCache(cache, designerId, 'Designer', 'mockupCount')
              evictFromCache(cache, projectId, 'Project', 'things')
            }
          },
        },
      )
      if (!response?.data?.mockup?.create?.record?.id) {
        // todo: something went wrong - log an error
        return
      } else {
        return response.data?.mockup.create.record.id
      }
    },
    updateName(input: MockupNameInput) {
      return updateName({ input })
    },
    updateNote(input: MockupNoteInput) {
      return updateNote({ input })
    },
    updateType(input: MockupTypeInput) {
      return updateType({ input })
    },
    updateProject(input: ProjectMockupInput) {
      return updateProject({ input })
    },
    updateData(input: MockupDataInput) {
      return updateData({ input })
    },
    delete(input: string) {
      return deleteVizi(
        { input },
        {
          refetchQueries: [{ query: getAccountsDataQuery }],
          update(cache) {
            evictFromCache(cache, input, 'Mockup')
          },
        },
      )
    },
    updateDailyPlay(input: MockupDailyPlayInput) {
      return updateDailyPlay({ input })
    },
    updateDesignChallenge(input: MockupDesignChallengeInput) {
      return updateDesignChallenge({ input })
    },
    sendReminder() {
      return sendReminder()
    },
    updateVisibility(input: MockupUpdateVisibilityInput) {
      return updateVisibility(input)
    },
    updateRoomType(input: RoomTypeInput) {
      return updateRoomType({ input })
    },
    updateTemplate(input: TemplateInput) {
      return updateTemplate({ input })
    },
  }
}
